import { FC, useMemo } from "react";
import { Tooltip, Typography } from "@mui/material";

import { PercentIndicatorCircle } from "components/PercentIndicatorCircle/PercentIndicatorCircle";

interface ActionStates {
  [key: string]: number;
  open_action_count: number;
  in_progress_action_count: number;
  in_review_action_count: number;
  completed_action_count: number;
  canceled_action_count: number;
}

interface Props {
  actionStates: ActionStates;
  size: number;
  strokeWidth: number;
}

const getPercent = (closed: number, total: number): number => {
  if (!closed && !total) {
    return 0;
  }
  return Math.floor((closed / total) * 100);
};

export const ProjectPercentIndicatorCircle: FC<Props> = ({
  actionStates,
  size,
  strokeWidth
}): JSX.Element => {
  let closed = 0;
  let total = 0;
  let percent = 0;
  let displayText = "—";
  let tooltipTitle = <>No actions</>;
  let fgcolor = "hsl(33, 96%, 54%)";

  if (Object.values(actionStates).some(state => state > 0)) {
    // we **deliberately** ignore `canceled_action_count` in closed and total counts
    closed = actionStates.completed_action_count;
    total =
      actionStates.open_action_count +
      actionStates.in_progress_action_count +
      actionStates.in_review_action_count +
      actionStates.completed_action_count;

    percent = getPercent(closed, total);
    displayText = `${percent}%`;
    tooltipTitle = <TooltipContent actionStates={actionStates} />;

    if (percent === 100) {
      // TODO: use the theme color
      fgcolor = "hsl(130, 61%, 39%)";
    }
  }

  return (
    <Tooltip title={tooltipTitle} placement="bottom-start">
      <PercentIndicatorCircle
        bgcolor="hsl(0, 0%, 56%)"
        fgcolor={fgcolor}
        displayText={displayText}
        percent={percent}
        size={size}
        strokeWidth={strokeWidth}
      />
    </Tooltip>
  );
};

export const TooltipContent: FC<{ actionStates: ActionStates }> = ({
  actionStates
}): JSX.Element => {
  const totalActions = useMemo(
    () =>
      actionStates.open_action_count +
      actionStates.in_progress_action_count +
      actionStates.in_review_action_count +
      actionStates.completed_action_count,
    [actionStates]
  );
  return (
    <>
      <Typography sx={{ fontSize: "1.2em" }}>
        Progress is based on the percentage of completed actions vs the total
        number of actions
      </Typography>

      <ul>
        <li>{`Total Actions: ${totalActions}`}</li>
        <li>{`Open: ${actionStates.open_action_count}`}</li>
        <li>{`In Progress: ${actionStates.in_progress_action_count}`}</li>
        <li>{`In Review: ${actionStates.in_review_action_count}`}</li>
        <li>{`Completed: ${actionStates.completed_action_count}`}</li>
        <li>{`Canceled*: ${actionStates.canceled_action_count}`}</li>
      </ul>

      <p>
        <em>
          * <strong>Canceled</strong> actions are not counted in the total
        </em>
      </p>
    </>
  );
};
